import { createStyles, makeStyles } from "@material-ui/core/styles";
import classnames from "classnames";
import * as React from "react";

import {
    Attachment,
    Button,
    DocViewer,
    docViewerUrl,
    DownloadIcon,
    ExternalLinkIcon,
    NoResultsText,
    PageActionButton,
    RelodeTheme,
    Switch,
    UploadIcon,
} from "../../";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            root: {
                padding: theme.spacing(2),
                height: "100%",
                flex: 1,
                display: "flex",
                flexDirection: "column",
            },
            controlBar: {
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
            },
            controls: {
                display: "flex",
                flex: 1,
            },
            controlsWithoutSwitch: {
                display: "flex",
                flex: 1,
                justifyContent: "space-evenly",
            },
            inputLabel: {
                display: "flex",
                flexDirection: "column",
                marginRight: theme.spacing(4),
            },
            switch: {},
            messages: {},
            docViewerWrapper: {
                flex: 1,
                marginTop: theme.spacing(2),
            },
            input: {
                display: "none",
            },
            saveChanges: {
                padding: theme.spacing(1),
            },
            downloadButton: {
                width: 180,
            },
            uploadButton: {
                display: "inline-flex",
                width: 180,
                marginRight: theme.spacing(1),
            },
            downloadIcon: {
                marginRight: theme.spacing(1),
            },
            uploadIcon: {
                marginRight: theme.spacing(1),
            },
            uploadLink: {
                marginRight: theme.spacing(2),
            },
            uploadError: {
                margin: 0,
            },
            message: {
                display: "flex",
                flexGrow: 1,
                justifyContent: "left",
                padding: 0,
                marginTop: 10,
            },
            hidden: {
                display: "none",
            },
            error: {
                color: theme.palette.error.main,
            },
            helperText: {
                marginBottom: theme.spacing(3),
            },
        }),
    { name: "LeadResume" }
);

interface LeadResumeProps {
    baseId: string;
    actionEnabled: boolean;
    onResumeUpload?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onFormattedResumeUpload?: (
        event: React.ChangeEvent<HTMLInputElement>
    ) => void;
    showSwitch?: boolean;
    initialShowFormattedResume?: boolean;
    downloadEnabled?: boolean;
    openExternalEnabled?: boolean;
    hidden?: boolean;
    values?: any;
    status?: any;
    downloadFormattedButtonLabel?: string;
    uploadFormattedButtonLabel?: string;
    uploadOriginalButtonLabel?: string;
    downloadOriginalButtonLabel?: string;
    displayDownloadAndOpenWindowStyle?: "buttons" | "icons";
    resume?: Attachment;
    formattedResume?: Attachment;
    error?: boolean;
    helperText?: string;
}

interface State {
    showFormattedResume: boolean;
}

export const LeadResume = (props: LeadResumeProps) => {
    const classes = useStyles({});
    const {
        downloadEnabled = true,
        downloadFormattedButtonLabel = "formatted",
        downloadOriginalButtonLabel = "original",
        displayDownloadAndOpenWindowStyle,
        openExternalEnabled,
        onFormattedResumeUpload,
        baseId,
        actionEnabled,
        uploadFormattedButtonLabel,
        values,
        onResumeUpload,
        uploadOriginalButtonLabel,
        hidden,
        showSwitch = true,
    } = props;

    const extendedId = `${baseId}LeadResume`;

    const { resume, formattedResume } = (() => {
        if (!!values) {
            return {
                resume: values.resume,
                formattedResume: values.formattedResume,
            };
        }

        return { resume: props.resume, formattedResume: props.formattedResume };
    })();
    //const { leadId, resume, formattedResume } = values;

    const resumeFileInput = React.useRef<HTMLInputElement>();
    const formattedResumeFileInput = React.useRef<HTMLInputElement>();
    const [showFormattedResume, setShowFormattedResume] = React.useState(
        props.initialShowFormattedResume
    );

    const handleSwitchToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
        setShowFormattedResume(e.target.checked);
    };

    const handleOpen = (resumeUrl: string) => {
        window.open(resumeUrl);
    };

    const renderResumeDownload = (resume: Attachment) => {
        const { fileUri } = resume;

        if (displayDownloadAndOpenWindowStyle !== "buttons") return null;

        if (downloadEnabled === true && showFormattedResume) {
            return (
                <>
                    <Button
                        className={classes.downloadButton}
                        onClick={() => handleOpen(fileUri)}
                        title="download formatted resume"
                    >
                        <DownloadIcon
                            color="inherit"
                            className={classes.downloadIcon}
                        />{" "}
                        {downloadFormattedButtonLabel}
                    </Button>
                </>
            );
        } else if (downloadEnabled === true) {
            return (
                <>
                    <Button
                        className={classes.downloadButton}
                        onClick={() => handleOpen(fileUri)}
                        title="download resume"
                    >
                        <DownloadIcon
                            color="inherit"
                            className={classes.downloadIcon}
                        />{" "}
                        {downloadOriginalButtonLabel}
                    </Button>
                </>
            );
        }
    };

    const renderPageActionButtons = (resume: Attachment) => {
        const { fileUri, contentType, fileExtension } = resume;

        if (displayDownloadAndOpenWindowStyle !== "icons") return null;

        return (
            <>
                {openExternalEnabled === true ? (
                    <PageActionButton
                        onClick={() =>
                            handleOpen(
                                docViewerUrl(
                                    fileUri,
                                    contentType,
                                    fileExtension
                                )
                            )
                        }
                        color="primary"
                        title="open in new window"
                    >
                        <ExternalLinkIcon color="inherit" />
                    </PageActionButton>
                ) : null}

                {downloadEnabled === true ? (
                    <PageActionButton
                        onClick={() => handleOpen(fileUri)}
                        color="primary"
                        title="download resume"
                    >
                        <DownloadIcon color="inherit" />
                    </PageActionButton>
                ) : null}
            </>
        );
    };

    const renderResumeUpload = (showFormattedResume: boolean) => {
        const extendedId = `${baseId}LeadResume`;

        if (showFormattedResume && onFormattedResumeUpload) {
            return (
                <label
                    className={classes.inputLabel}
                    htmlFor={`${extendedId}FormattedResumeInput`}
                >
                    <input
                        ref={formattedResumeFileInput}
                        accept=".docx"
                        className={classes.input}
                        type="file"
                        onChange={handleResumeUpload(true)}
                        name="formatted-resume-input"
                        id={`${extendedId}FormattedResumeInput`}
                        disabled={!actionEnabled}
                    />
                    <Button
                        color="default"
                        variant="contained"
                        size="small"
                        component="span"
                        className={classes.uploadButton}
                        title="upload formatted resume"
                        disabled={!actionEnabled}
                    >
                        <UploadIcon
                            color="inherit"
                            className={classes.uploadIcon}
                        />
                        {uploadFormattedButtonLabel}
                    </Button>
                </label>
            );
        }
        if (!showFormattedResume && onResumeUpload) {
            return (
                <label
                    className={classes.inputLabel}
                    htmlFor={`${extendedId}ResumeInput`}
                >
                    <input
                        ref={resumeFileInput}
                        accept=".doc,.docx,.pdf,.rtf,.txt,.html,.htm"
                        className={classes.input}
                        type="file"
                        onChange={handleResumeUpload()}
                        name="resume-input"
                        id={`${extendedId}ResumeInput`}
                        disabled={!actionEnabled}
                    />
                    <Button
                        color="default"
                        variant="contained"
                        size="small"
                        component="span"
                        className={classes.uploadButton}
                        title="upload original resume"
                        disabled={!actionEnabled}
                    >
                        <UploadIcon
                            color="inherit"
                            className={classes.uploadIcon}
                        />
                        {uploadOriginalButtonLabel}
                    </Button>
                </label>
            );
        }
        return null;
    };

    const clearFileInput = (isFormatted?: boolean) => {
        if (isFormatted && formattedResumeFileInput.current) {
            formattedResumeFileInput.current.value = "";
        } else if (resumeFileInput.current) {
            resumeFileInput.current.value = "";
        }
    };

    const handleResumeUpload = (isFormatted?: boolean) => async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        if (isFormatted && onFormattedResumeUpload) {
            await onFormattedResumeUpload(event);
        } else if (onResumeUpload) {
            await onResumeUpload(event);
        }

        clearFileInput(isFormatted);
    };

    const rootClass = classnames({
        [classes.root]: true,
        [classes.hidden]: !!hidden,
    });

    // determine resume we are showing
    const resumeFile: Attachment | null =
        showFormattedResume && formattedResume
            ? formattedResume
            : !showFormattedResume && resume
            ? resume
            : null;

    return (
        <div className={rootClass} id={`${extendedId}Wrapper`}>
            <div
                className={classes.controlBar}
                id={`${extendedId}ResumeControlBar`}
            >
                {showSwitch ? (
                    <div className={classes.controls}>
                        {renderResumeUpload(showFormattedResume)}
                        {resumeFile ? renderResumeDownload(resumeFile) : null}
                        {resumeFile
                            ? renderPageActionButtons(resumeFile)
                            : null}
                    </div>
                ) : (
                    <div className={classes.controlsWithoutSwitch}>
                        {renderResumeUpload(showFormattedResume)}
                        {resumeFile ? renderResumeDownload(resumeFile) : null}
                        {resumeFile
                            ? renderPageActionButtons(resumeFile)
                            : null}
                    </div>
                )}

                {!actionEnabled ? (
                    <div className={classes.message}>
                        <NoResultsText
                            className={classes.saveChanges}
                            padding={false}
                        >
                            Please save changes to enable upload.
                        </NoResultsText>
                    </div>
                ) : null}
                {showSwitch ? (
                    <div
                        className={classes.switch}
                        id={`${extendedId}ResumeFormattedSwitchWrapper`}
                    >
                        <Switch
                            id={`${extendedId}ResumeFormattedSwitch`}
                            checked={showFormattedResume}
                            onSwitchChange={handleSwitchToggle}
                            label="formatted"
                            labelPlacement="end"
                        />
                    </div>
                ) : null}
            </div>

            {resumeFile ? (
                <div
                    className={classes.docViewerWrapper}
                    id={`${extendedId}ResumeDocViewerWrapper`}
                >
                    <DocViewer
                        docKey={resumeFile.fileId}
                        docUrl={resumeFile.fileUri}
                        docType={resumeFile.contentType}
                        docExtension={resumeFile.fileExtension}
                    />
                </div>
            ) : null}
        </div>
    );
};
